// Toast — floating notification primitive for transient admin feedback.
//
// Provides:
//   - ToastProvider: context provider + toast stack renderer. Admin-only
//     (brand gate — do not use on capture or tenant surfaces).
//   - useToast: hook that returns showSuccess / showError helpers.
//
// Accessibility:
//   - success toasts use role="status"  (polite announcement)
//   - error toasts use role="alert"     (assertive / immediate announcement)
//
// Auto-dismiss: 4 000 ms. Manual dismiss button always present.
// Stacking: newest toast appears at the bottom of the stack (FIFO visual
//           order). Stack is capped at 5 to prevent unbounded growth.
//
// Touch target: dismiss button is 44×44 minimum per AGENTS.md invariant #10.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { X } from 'lucide-react'

// ----- Types -------------------------------------------------------

export type ToastVariant = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: string
  variant: ToastVariant
  message: string
}

export interface ToastContextValue {
  /** Show a success toast. Auto-dismisses after 4 s. */
  showSuccess: (message: string) => void
  /** Show an error toast. Auto-dismisses after 4 s. */
  showError: (message: string) => void
  /** Show an info toast. Auto-dismisses after 4 s. */
  showInfo: (message: string) => void
  /** Show a warning toast. Auto-dismisses after 4 s. */
  showWarning: (message: string) => void
}

// ----- Context -----------------------------------------------------

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

/** Returns the toast API. Works inside `<ToastProvider>` only. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (ctx === undefined) {
    // Graceful fallback: components outside the provider (tests, capture
    // surfaces) will get no-op functions rather than crashing.
    return {
      showSuccess: () => {},
      showError: () => {},
      showInfo: () => {},
      showWarning: () => {},
    }
  }
  return ctx
}

// ----- Constants ---------------------------------------------------

const AUTO_DISMISS_MS = 4_000
const MAX_STACK = 5

// ----- Provider ----------------------------------------------------

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  // Keep a ref so callbacks don't stale-close over the setter.
  const counterRef = useRef(0)

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const add = useCallback(
    (variant: ToastVariant, message: string) => {
      counterRef.current += 1
      const id = `toast-${counterRef.current}`
      setToasts((prev) => {
        const next: Toast[] = [...prev, { id, variant, message }]
        // Cap stack — drop oldest entries from the front.
        return next.length > MAX_STACK ? next.slice(next.length - MAX_STACK) : next
      })
    },
    [],
  )

  const showSuccess = useCallback(
    (message: string) => add('success', message),
    [add],
  )
  const showError = useCallback(
    (message: string) => add('error', message),
    [add],
  )
  const showInfo = useCallback(
    (message: string) => add('info', message),
    [add],
  )
  const showWarning = useCallback(
    (message: string) => add('warning', message),
    [add],
  )

  return (
    <ToastContext.Provider value={{ showSuccess, showError, showInfo, showWarning }}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

// ----- Stack renderer ----------------------------------------------

interface ToastStackProps {
  toasts: Toast[]
  onDismiss: (id: string) => void
}

function ToastStack({ toasts, onDismiss }: ToastStackProps) {
  if (toasts.length === 0) return null
  return (
    // Fixed bottom-right corner; z-50 sits above dialogs (z-40) and
    // the nav drawer (z-50 on mobile) but below any system chrome.
    <div
      aria-label="Notifications"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

// ----- Single item -------------------------------------------------

const VARIANT_CLASSES: Record<ToastVariant, string> = {
  success: 'border-brand bg-brand text-brand-foreground',
  error: 'border-red-200 bg-red-50 text-red-900',
  info: 'border-brand-deep bg-surface text-ink',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
}

const DISMISS_BUTTON_CLASSES: Record<ToastVariant, string> = {
  success: 'text-brand-foreground hover:bg-brand-foreground/10',
  error: 'text-red-900 hover:bg-red-100',
  info: 'text-ink hover:bg-ink/10',
  warning: 'text-amber-900 hover:bg-amber-100',
}

interface ToastItemProps {
  toast: Toast
  onDismiss: (id: string) => void
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const { id, variant, message } = toast

  useEffect(() => {
    const timer = setTimeout(() => onDismiss(id), AUTO_DISMISS_MS)
    return () => clearTimeout(timer)
  }, [id, onDismiss])

  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      data-testid="toast"
      className={[
        'pointer-events-auto flex min-w-[280px] max-w-sm items-start gap-3',
        'rounded-md border px-4 py-3 text-sm shadow-md',
        VARIANT_CLASSES[variant],
      ].join(' ')}
    >
      <span className="flex-1">{message}</span>
      <button
        type="button"
        onClick={() => onDismiss(id)}
        aria-label="Dismiss notification"
        className={[
          'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md',
          DISMISS_BUTTON_CLASSES[variant],
        ].join(' ')}
      >
        <X aria-hidden="true" className="h-4 w-4" />
      </button>
    </div>
  )
}
